/**
 * Financial Analytics Component
 * Phase 1: Foundation & Core Analytics
 */

import React from 'react';
import { 
  DollarSign, 
  TrendingUp, 
  Target,
  Award,
  BarChart3,
  PieChart
} from 'lucide-react';
import { FinancialAnalytics as FinancialAnalyticsType } from '../../services/analytics';

interface FinancialAnalyticsProps {
  data: FinancialAnalyticsType;
  loading?: boolean;
}

const FinancialAnalytics: React.FC<FinancialAnalyticsProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000000) {
      return `€${(amount / 1000000000).toFixed(1)}B`;
    } else if (amount >= 1000000) {
      return `€${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `€${(amount / 1000).toFixed(1)}K`;
    }
    return `€${amount.toFixed(0)}`;
  };

  const formatNumber = (value: number | string | null | undefined, decimals: number = 1): string => {
    if (value === null || value === undefined) return '0';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return num.toFixed(decimals);
  };

  const getROIColor = (roi: number) => {
    if (roi >= 50) return 'text-green-600';
    if (roi >= 25) return 'text-yellow-600';
    if (roi >= 10) return 'text-orange-600';
    return 'text-red-600';
  };

  const getROIBarColor = (roi: number) => {
    if (roi >= 50) return 'bg-green-500';
    if (roi >= 25) return 'bg-yellow-500';
    if (roi >= 10) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6">
      {/* Financial Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Estimated Savings */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Estimated Savings</p>
              <p className="text-3xl font-bold text-blue-600">{formatCurrency(Number(data.total_estimated_savings))}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Total Released Savings */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Released Savings</p>
              <p className="text-3xl font-bold text-green-600">{formatCurrency(Number(data.total_released_savings))}</p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Realization Rate */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Realization Rate</p>
              <p className="text-3xl font-bold text-purple-600">{formatNumber(data.realization_rate)}%</p>
            </div>
            <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Target className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Savings by CC Category */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Savings by CC Category</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(data.savings_by_cc_category).map(([category, metrics]) => (
            <div key={category} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">{category}</span>
                <span className="text-sm text-gray-600">{formatNumber(metrics.realization_rate)}% realized</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Estimated</span>
                  <span className="font-semibold text-blue-600">{formatCurrency(metrics.estimated)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Released</span>
                  <span className="font-semibold text-green-600">{formatCurrency(metrics.released)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${Math.min(metrics.realization_rate, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Savings by SQDCM Category */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Savings by SQDCM Category</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(data.savings_by_sqdcm_category).map(([category, metrics]) => (
            <div key={category} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">{category}</span>
                <span className="text-sm text-gray-600">{formatNumber(metrics.realization_rate)}% realized</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Estimated</span>
                  <span className="font-semibold text-blue-600">{formatCurrency(metrics.estimated)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Released</span>
                  <span className="font-semibold text-green-600">{formatCurrency(metrics.released)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: `${Math.min(metrics.realization_rate, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Savings by Workstream */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Savings by Workstream</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(data.savings_by_workstream).map(([workstream, metrics]) => (
            <div key={workstream} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">{workstream}</span>
                <span className="text-sm text-gray-600">{metrics.realization_rate.toFixed(1)}% realized</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Estimated</span>
                  <span className="font-semibold text-blue-600">{formatCurrency(metrics.estimated)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Released</span>
                  <span className="font-semibold text-green-600">{formatCurrency(metrics.released)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-500 h-2 rounded-full"
                    style={{ width: `${Math.min(metrics.realization_rate, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Performing Workshops */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Workshops by ROI</h3>
        <div className="space-y-3">
          {data.top_performing_workshops.slice(0, 10).map((workshop, index) => (
            <div key={workshop.workshop_number} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
                  <span className="text-sm font-semibold text-blue-600">#{workshop.workshop_number}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{workshop.title}</p>
                  <div className="flex items-center space-x-4 text-xs text-gray-600">
                    <span>Est: {formatCurrency(workshop.estimated_savings)}</span>
                    <span>Rel: {formatCurrency(workshop.released_savings)}</span>
                    <span>Progress: {formatNumber(workshop.progress)}%</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Award className="h-4 w-4 text-yellow-600" />
                <span className={`text-sm font-semibold ${getROIColor(Number(workshop.roi))}`}>
                  {formatNumber(workshop.roi)}% ROI
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly Savings Trend */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Savings Trend</h3>
        <div className="space-y-3">
          {data.monthly_savings_trend.slice(-6).map((trend, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-700">{trend.month}</span>
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-gray-600">Est: {formatCurrency(trend.estimated_savings)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-gray-600">Rel: {formatCurrency(trend.released_savings)}</span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Target className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-semibold text-purple-600">
                  {formatNumber(trend.realization_rate)}% realized
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Average ROI */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <PieChart className="h-6 w-6 text-purple-600" />
            <span className="text-lg font-semibold text-gray-900">Average ROI</span>
          </div>
          <p className={`text-4xl font-bold ${getROIColor(Number(data.average_roi))}`}>
            {formatNumber(data.average_roi)}%
          </p>
          <p className="text-sm text-gray-600 mt-2">Across all workshops</p>
        </div>
      </div>
    </div>
  );
};

export default FinancialAnalytics;
